import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import LazyImage from "components/lazy-image";
import { PUBLIC_ASSETS } from "constants/publicAssets";
import moment from "moment-timezone";
import React, { forwardRef, useCallback, useMemo } from "react";
import getClasses from "utils/getClasses";
import SessionIndicatorIcons, {
  SessionIndicatorIconsData,
} from "../../../session-indicator-icons/SessionIndicatorIcons";
import styles from "./TimeslotButton.module.scss";
import { getSessionLengthCategory } from "./get-session-length-category";
import formatSessionDuration from "utils/formatSessionDuration";
import { CalendarColumnPosition } from "../../utils/splitEventsIntoColumns";

interface TimeslotButtonProps {
  style?: React.CSSProperties;
  startDate?: string;
  endDate?: string;
  typeIndicatorColor?: React.CSSProperties["backgroundColor"];
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  timeslotName?: string;
  showGCalIcon?: boolean;
  isFocused?: boolean;
  isCancelled?: boolean;
  isPlaceholder?: boolean;
  className?: string;
  sessionIndicatorIconsData?: SessionIndicatorIconsData;
  date?: Date;
  calendarColumnPosition?: CalendarColumnPosition;
}

const getHorizontalPosition = (
  calendarColumnPosition: CalendarColumnPosition,
) => {
  const { totalColumns, columnIndex } = calendarColumnPosition;
  const marginLeft = "2px";
  const margins = "5px"; // 2px left + 3px right
  const minWidth = "60px";

  const slotSpaceWhenFew = `calc((100% - ${margins}) / ${totalColumns})`;
  const slotSpaceWhenMany = `calc((100% - ${margins} - ${minWidth}) / ${
    totalColumns - 1
  })`;
  const slotSpace = `min(${slotSpaceWhenFew}, ${slotSpaceWhenMany})`;

  const left = `calc(${slotSpace} * ${columnIndex} + ${marginLeft})`;
  const width = `max(calc(100% / ${totalColumns} - ${margins}), ${minWidth})`;
  return {
    left,
    width,
  };
};

const getHeightInHours = (
  eventStartsToday: boolean,
  eventEndsToday: boolean,
  totalSessionMinutes: number,
  start: moment.Moment,
  end: moment.Moment,
) => {
  if (eventStartsToday) {
    const sessionLengthHours = totalSessionMinutes / 60;
    const maxHeightInHours = 24 - start.hours() - start.minutes() / 60;
    return Math.min(sessionLengthHours, maxHeightInHours);
  }

  if (eventEndsToday) {
    return end.hours() + end.minutes() / 60;
  }

  return 24;
};

const TimeslotButton = forwardRef(
  (
    {
      className = "",
      onClick,
      style = {},
      startDate = "",
      endDate = "",
      typeIndicatorColor = "",
      timeslotName = "",
      showGCalIcon = false,
      isFocused = false,
      isCancelled = false,
      isPlaceholder = false,
      sessionIndicatorIconsData,
      date,
      calendarColumnPosition = {
        totalColumns: 1,
        columnIndex: 0,
      },
      ...props
    }: TimeslotButtonProps,
    ref,
  ) => {
    const { currentCalendarTimezone, currentTimeRangeType } = useCalendarTime();

    const start = useMemo(
      () => moment.tz(startDate, currentCalendarTimezone),
      [startDate, currentCalendarTimezone],
    );

    const end = useMemo(
      () => moment.tz(endDate, currentCalendarTimezone),
      [endDate, currentCalendarTimezone],
    );

    const { eventStartsToday, eventEndsToday } = useMemo(() => {
      const currentDayStartOfDayMoment = moment
        .tz(date, currentCalendarTimezone)
        .startOf("day");
      const eventStartsToday =
        !date ||
        start.clone().startOf("day").isSame(currentDayStartOfDayMoment);
      const eventEndsToday =
        !date || end.clone().startOf("day").isSame(currentDayStartOfDayMoment);

      return {
        eventStartsToday,
        eventEndsToday,
      };
    }, [date, start, end, currentCalendarTimezone]);

    const totalSessionMinutes = useMemo(
      () => end.diff(start, "minutes", true),
      [start, end],
    );

    const currentDaySessionMinutes = useMemo(() => {
      if (eventStartsToday && eventEndsToday) {
        return totalSessionMinutes;
      }

      if (eventStartsToday) {
        return moment
          .tz(date, currentCalendarTimezone)
          .add(1, "day")
          .startOf("day")
          .diff(start, "minutes", true);
      }

      if (eventEndsToday) {
        return end.hours() * 60 + end.minutes();
      }

      return 24 * 60;
    }, [
      eventStartsToday,
      eventEndsToday,
      totalSessionMinutes,
      start,
      end,
      date,
      currentCalendarTimezone,
    ]);

    const timeslotSizeStyleProperties: React.CSSProperties = useMemo(() => {
      if (currentTimeRangeType === "month") {
        return {};
      }

      const heightInHours = getHeightInHours(
        eventStartsToday,
        eventEndsToday,
        totalSessionMinutes,
        start,
        end,
      );

      /**
       * based on mins percentage in the hour
       * @example 2:18 -> 18 / 60 -> 30%
       */
      const top = eventStartsToday
        ? `calc(${(start.minutes() / 60) * 100}% + 1px)`
        : 0;

      const heightAdjustmentForSpaceBetweenBackToBackSessions = eventStartsToday
        ? -2
        : -1;

      const heightAdjustmentForCellBordersWithinSession =
        Math.ceil(heightInHours) - 1;

      const height = `calc(${heightInHours * 100}%  + ${
        heightAdjustmentForCellBordersWithinSession +
        heightAdjustmentForSpaceBetweenBackToBackSessions
      }px)`;

      return {
        top,
        height,
        ...getHorizontalPosition(calendarColumnPosition),
      };
    }, [
      currentTimeRangeType,
      start,
      end,
      totalSessionMinutes,
      eventStartsToday,
      eventEndsToday,
      calendarColumnPosition,
    ]);

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!onClick) {
          return;
        }

        onClick(event);
      },
      [onClick],
    );

    const sessionLengthCategory = useMemo(
      () => getSessionLengthCategory(currentDaySessionMinutes),
      [currentDaySessionMinutes],
    );

    const showFullSessionTime = useMemo(
      () =>
        !isPlaceholder &&
        startDate &&
        endDate &&
        currentTimeRangeType !== "month" &&
        sessionLengthCategory === "long",
      [
        isPlaceholder,
        startDate,
        endDate,
        currentTimeRangeType,
        sessionLengthCategory,
      ],
    );

    const timeLabel = useMemo(
      () =>
        showFullSessionTime
          ? `${start.format("h:mm")} - ${end.format(
              "h:mm A",
            )} (${formatSessionDuration(totalSessionMinutes)})`
          : start.format("h:mm A"),
      [start, end, totalSessionMinutes, showFullSessionTime],
    );

    const showAsSingleLine = useMemo(
      () => currentTimeRangeType === "month" || currentDaySessionMinutes < 16,
      [currentTimeRangeType, currentDaySessionMinutes],
    );

    const maxDisplayedIcons = useMemo(
      () => (currentTimeRangeType === "day" ? 4 : 2),
      [currentTimeRangeType],
    );

    const containerClassName = useMemo(
      () =>
        getClasses(
          styles["container"],
          currentTimeRangeType === "month" && styles["container--monthly"],
          currentTimeRangeType === "week" && styles["container--weekly"],
          currentTimeRangeType === "week" &&
            styles[`container--weekly--${sessionLengthCategory}`],
          currentTimeRangeType === "day" && styles["container--daily"],
          currentTimeRangeType === "day" &&
            styles[`container--daily--${sessionLengthCategory}`],
          isFocused && styles["container--focused"],
          isCancelled && styles["container--cancelled"],
          "smoothed",
          className,
        ),
      [
        currentTimeRangeType,
        sessionLengthCategory,
        isFocused,
        isCancelled,
        className,
      ],
    );

    if (currentDaySessionMinutes <= 0) {
      return null;
    }

    return (
      <div
        ref={ref as any}
        className={containerClassName}
        onClick={handleClick}
        style={{ ...style, ...timeslotSizeStyleProperties }}
        {...props}
      >
        {typeIndicatorColor && (
          <div
            className={getClasses(styles["type-indicator"])}
            style={{ backgroundColor: typeIndicatorColor }}
          />
        )}

        {showGCalIcon && (
          <div className={styles["gcal-icon"]}>
            <LazyImage src={PUBLIC_ASSETS.gcalIcon} width={10} height={10} />
          </div>
        )}

        {showAsSingleLine ? (
          <>
            {currentTimeRangeType === "month" && (
              <div className={styles["start-time"]}>{timeLabel}</div>
            )}
            <div className={styles["name"]}>{timeslotName}</div>
            <div className={styles["spacer"]}></div>
            {sessionIndicatorIconsData &&
              (currentTimeRangeType === "month" ||
                sessionLengthCategory !== "extra-short") && (
                <SessionIndicatorIcons
                  maxDisplayedIcons={maxDisplayedIcons}
                  data={sessionIndicatorIconsData}
                />
              )}
          </>
        ) : (
          <div className={styles["text-content"]}>
            <div
              className={getClasses(
                styles["name"],
                currentDaySessionMinutes < 33 &&
                  styles["name--force-single-line"],
              )}
            >
              {timeslotName}
            </div>
            {showFullSessionTime && (
              <div
                className={getClasses(
                  styles["full-time"],
                  currentDaySessionMinutes < 45 &&
                    styles["full-time--force-single-line"],
                )}
              >
                {timeLabel}
              </div>
            )}
            {sessionIndicatorIconsData && (
              <>
                <div className={styles["spacer"]}></div>
                <SessionIndicatorIcons
                  maxDisplayedIcons={maxDisplayedIcons}
                  data={sessionIndicatorIconsData}
                />
              </>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default TimeslotButton;
